import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  FiUsers,
  FiCalendar,
  FiBookOpen,
  FiClock,
  FiLayers,
  FiSearch,
  FiRefreshCcw,
} from 'react-icons/fi'
import { fetchStaffDashboard } from '../../store/slices/teacherSlice'
import { fetchAssignedBatches as fetchAssignedBatchesApi } from '../../api/staffTeachingApi'
import './batches.css'

const formatLectureWindow = (startTime, endTime) => {
  if (!startTime) return { slot: 'Pending scheduling', duration: '—' }
  const start = new Date(startTime)
  const end = endTime ? new Date(endTime) : null
  const today = new Date()
  const label = start.toDateString() === today.toDateString()
    ? 'Today'
    : start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  const duration = end ? `${Math.max(1, Math.round((end - start) / 60000))} min` : '—'
  return {
    slot: `${label} · ${start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`,
    duration,
  }
}

const normaliseStatus = (status) => {
  if (!status) return 'Scheduled'
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const Batches = () => {
  const dispatch = useDispatch()
  const { dashboard, isLoading, error, lastUpdated } = useSelector((state) => state.teacher)
  const [query, setQuery] = useState('')
  const [assignedBatches, setAssignedBatches] = useState([])
  const [teachingLoading, setTeachingLoading] = useState(false)
  const [teachingError, setTeachingError] = useState(null)
  const teachingFetchInFlight = useRef(false)

  const loadAssignedBatches = useCallback(async () => {
    if (teachingFetchInFlight.current) return
    teachingFetchInFlight.current = true
    setTeachingLoading(true)
    setTeachingError(null)
    try {
      const data = await fetchAssignedBatchesApi()
      setAssignedBatches(Array.isArray(data) ? data : [])
    } catch (apiError) {
      console.error('Failed to load assigned batches', apiError)
      setTeachingError(apiError?.response?.data?.message || apiError.message || 'Unable to fetch batches')
    } finally {
      setTeachingLoading(false)
      teachingFetchInFlight.current = false
    }
  }, [])

  useEffect(() => {
    if (!dashboard && !isLoading) {
      dispatch(fetchStaffDashboard())
    }
  }, [dashboard, isLoading, dispatch])

  useEffect(() => {
    loadAssignedBatches()
  }, [loadAssignedBatches])

  const batches = useMemo(() => {
    if (assignedBatches.length) {
      return assignedBatches.map((batch) => {
        const nextLectureWindow = batch.nextLecture
          ? formatLectureWindow(batch.nextLecture.startTime, batch.nextLecture.endTime)
          : { slot: 'No upcoming session', duration: '—' }

        return {
          id: batch._id,
          name: batch.batchName,
          course: batch.course?.title || 'Unassigned course',
          status: normaliseStatus(batch.status),
          students: batch.studentCount ?? 0,
          modules: batch.moduleCount ?? 0,
          nextLecture: nextLectureWindow,
          level: batch.course?.level || 'Programme',
        }
      })
    }

    const fallback = dashboard?.assignedBatches || dashboard?.managedBatches || []
    return fallback.map((batch) => {
      const courseTitle = batch.course?.title || 'Unassigned course'
      const nextLecture = batch.nextLecture
        ? formatLectureWindow(batch.nextLecture.startTime, batch.nextLecture.endTime)
        : { slot: 'No upcoming session', duration: '—' }

      return {
        id: batch._id,
        name: batch.batchName,
        course: courseTitle,
        status: normaliseStatus(batch.status),
        students: batch.studentCount ?? 0,
        modules: batch.modules?.length ?? batch.moduleCount ?? 0,
        nextLecture,
        level: batch.course?.level || batch.course?.category || 'Programme',
      }
    })
  }, [assignedBatches, dashboard])

  const filteredBatches = useMemo(() => {
    if (!query.trim()) return batches
    const value = query.trim().toLowerCase()
    return batches.filter((batch) =>
      [batch.name, batch.course, batch.status, batch.level]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(value))
    )
  }, [batches, query])

  const summaryMetrics = useMemo(() => {
    if (!dashboard && !assignedBatches.length) {
      return [
        { label: 'Active batches', value: '—', icon: FiLayers },
        { label: 'Students enrolled', value: '—', icon: FiUsers },
        { label: 'Course tracks', value: '—', icon: FiBookOpen },
        { label: 'Upcoming sessions', value: '—', icon: FiCalendar },
      ]
    }

    const totalStudents = batches.reduce((sum, batch) => sum + (batch.students || 0), 0)
    const uniqueCourses = new Set(batches.map((batch) => batch.course)).size
    const upcomingSessions = batches.reduce(
      (sum, batch) => sum + (batch.nextLecture.slot === 'No upcoming session' ? 0 : 1),
      0
    )

    const batchCount =
      dashboard?.stats?.assignedBatches ??
      dashboard?.stats?.managedBatches ??
      dashboard?.stats?.totalBatches ??
      batches.length

    return [
      { label: 'Active batches', value: batchCount, icon: FiLayers },
      { label: 'Students enrolled', value: totalStudents, icon: FiUsers },
      { label: 'Course tracks', value: uniqueCourses, icon: FiBookOpen },
      { label: 'Upcoming sessions', value: upcomingSessions, icon: FiCalendar },
    ]
  }, [dashboard, batches])

  const handleRefresh = () => {
    const actions = []
    if (!isLoading) {
      actions.push(dispatch(fetchStaffDashboard()))
    }
    actions.push(loadAssignedBatches())
    return Promise.all(actions)
  }

  const lastSyncedLabel = lastUpdated
    ? new Date(lastUpdated).toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric',
      })
    : 'Not synced yet'

  return (
    <div className="batches-page">
      <header className="batch-header">
        <div>
          <p className="eyebrow">Batch coordination</p>
          <h1>All assigned batches</h1>
          <p className="subhead">Monitor student momentum and stay ahead of upcoming sessions.</p>
        </div>
        <div className="batch-actions">
          <button
            type="button"
            className="refresh-button"
            onClick={handleRefresh}
            disabled={isLoading || teachingLoading}
          >
            <FiRefreshCcw size={16} />
            {isLoading || teachingLoading ? 'Refreshing…' : 'Refresh data'}
          </button>
          <span className="last-synced">Last synced · {lastSyncedLabel}</span>
        </div>
      </header>

      <section className="batch-summary">
        {summaryMetrics.map((metric) => {
          const Icon = metric.icon
          return (
            <article key={metric.label} className="summary-card">
              <span className="summary-icon">
                <Icon size={18} />
              </span>
              <div>
                <p>{metric.label}</p>
                <strong>{metric.value}</strong>
              </div>
            </article>
          )
        })}
      </section>

      <div className="batch-toolbar">
        <label className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search batches, courses, or status"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <span className="result-count">{filteredBatches.length} batches</span>
      </div>

      {(teachingError || error) && (
        <div className="batch-error" role="alert">
          Unable to load batch data: {teachingError || error}
        </div>
      )}

      {(teachingLoading || (isLoading && !dashboard)) && batches.length === 0 ? (
        <div className="batch-state">Loading batches…</div>
      ) : filteredBatches.length === 0 ? (
        <div className="batch-state">No batches match that search. Try another keyword.</div>
      ) : (
        <section className="batch-grid">
          {filteredBatches.map((batch) => (
            <article key={batch.id} className="batch-card">
              <header className="batch-card__header">
                <div>
                  <h2>{batch.name}</h2>
                  <p>{batch.course}</p>
                </div>
                <span className="batch-status">{batch.status}</span>
              </header>

                <div className="batch-card__metrics">
                <div>
                  <FiUsers size={18} />
                  <div>
                    <span>Students</span>
                    <strong>{batch.students}</strong>
                  </div>
                </div>
                <div>
                  <FiBookOpen size={18} />
                  <div>
                    <span>Modules</span>
                    <strong>{batch.modules}</strong>
                  </div>
                </div>
                <div>
                  <FiClock size={18} />
                  <div>
                    <span>Next session</span>
                    <strong>{batch.nextLecture.slot}</strong>
                  </div>
                </div>
              </div>

              <footer className="batch-card__footer">
                <div className="batch-timing">Duration · {batch.nextLecture.duration}</div>
                <Link to={`/batches/${batch.id}`} className="batch-link">
                  View details
                </Link>
              </footer>
            </article>
          ))}
        </section>
      )}
    </div>
  )
}

export default Batches